import { creatorbase } from '@round/api';
import { Form, useFormikContext } from 'formik';
import { FormField, ImageUploadField, SelectField } from '@round/whitelabel-ui';
import * as yup from 'yup';
import styles from './ReportForm.module.css';
import useTeamSelect, { TeamOption } from 'Modules/Plans/Team/hooks/useTeamSelect';
import omit from 'lodash/omit';
import { GenericDropdownOption } from 'App.types';
import { useCreatorbaseCurrencies } from 'contexts/CreatorbaseCurrencyContext';
import { ValueType } from 'react-select';
import { UserOption, useUsersMultiSelect } from '../../../User/hooks/useUsersSelect';
import { ExternalMultiValuesControl } from '@round/whitelabel-ui';
import { StaticTextValue } from '@round/whitelabel-ui';
import { None } from '@round/whitelabel-ui';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { MEGA_BYTE, SUPPORTED_IMAGE_EXTENSIONS } from 'utility/constants';

export type FormValues = Pick<creatorbase.PostReportData, 'description' | 'name' | 'project_ids'> & {
    team: TeamOption | null;
    currency: GenericDropdownOption<number> | null;
    assignees: ValueType<UserOption, true>;
    image: File | string | null;
};

export const validationSchema = yup.object({
    name: yup.string().required('Name is required'),
    team: yup.object().nullable().required('Team is required'),
    currency: yup.object().nullable().required('Currency is required'),
    image: yup
        .mixed()
        .nullable()
        .test('fileSize', 'File is too large. Max size: 5 MB', (value: File | null | undefined) => {
            return (value?.size ?? 0) <= 5 * MEGA_BYTE;
        }),
});

type Props = {
    disabledFields?: { [key in keyof FormValues]?: boolean };
};

const ReportForm = ({ disabledFields }: Props) => {
    const isCurrentUserCreatorbaseAdmin = useCheckUserGroupsAccess(['creatorbase_admin'], 'all');
    const { selectedClientId } = useClientSelect();

    const { creatorbaseUserId } = useCurrentCreatorbaseUser();

    const formik = useFormikContext<FormValues>();
    const { currencies } = useCreatorbaseCurrencies();
    const currencyOptions = currencies.map<GenericDropdownOption<number>>((c) => ({ value: c.id, label: c.name }));

    const { getProps: getTeamSelectProps } = useTeamSelect({
        initOn: 'mount',
        params: {
            user_with_direct_access_id: creatorbaseUserId || undefined,
            client_id: selectedClientId,
        },
        enabled: typeof selectedClientId === 'number',
    });

    const { getProps: getAssigneesProps } = useUsersMultiSelect({
        initOn: 'menuOpen',
        params: {
            team_id: formik.values.team?.value,
            ...(!isCurrentUserCreatorbaseAdmin && { is_creatorbase_admin: false }),
        },
    });

    const isRemovable = (option: UserOption) => {
        if (isCurrentUserCreatorbaseAdmin) {
            return true;
        }
        return !option.is_creatorbase_admin;
    };

    return (
        <Form className={styles.form}>
            <ImageUploadField name="image" options={{ accept: SUPPORTED_IMAGE_EXTENSIONS }} />
            <FormField name="name" label="Name" placeholder="Enter a report name" disabled={disabledFields?.['name']} />

            <FormField
                name="description"
                label="Description"
                placeholder="Enter a report description"
                component="textarea"
                disabled={disabledFields?.['description']}
            />

            <SelectField
                {...omit(getTeamSelectProps(), ['value', 'onChange'])}
                name="team"
                label="Team"
                placeholder="Select a team"
                onChange={() => {
                    formik.setFieldValue('assignees', [], false);
                }}
                disabled={disabledFields?.['team']}
            />

            <SelectField
                name="currency"
                label="Currency"
                placeholder="Select a currency"
                options={currencyOptions}
                disabled={disabledFields?.['currency']}
            />

            <SelectField
                name="assignees"
                label="Assignees"
                placeholder="Select assignees"
                disabled={!formik.values.team || disabledFields?.['assignees']}
                isClearable={false}
                controlShouldRenderValue={false}
                {...omit(getAssigneesProps(), ['value', 'onChange'])}
                components={{
                    Control: ExternalMultiValuesControl,
                    Input: StaticTextValue,
                    MultiValue: None,
                    Placeholder: None,
                }}
                customSelectProps={{
                    isRemovable: isRemovable,
                }}
            />
        </Form>
    );
};

export default ReportForm;
